// import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
// import Cookies from "js-cookie";
// import { fetchRoleAccess } from "../configuration/module/UserAccess/Service/Useraccessservice";

// // ─── Context ─────────────────────────────────────────────────────────────────
// const PermissionContext = createContext({
//   permissions: {},       // { CHECKLIST_BUILDER: { read, create, update, delete }, … }
//   loading: true,
//   reload: () => {},
//   can: () => false,
// });

// // ─── Helper: parse user_details cookie ───────────────────────────────────────
// const getUserDetails = () => {
//   try {
//     const raw = Cookies.get("user_details");
//     return raw ? JSON.parse(raw) : null;
//   } catch {
//     return null;
//   }
// };

// // ─── Helper: flatten API list → map keyed by menuKey ─────────────────────────
// const buildPermMap = (list = []) => {
//   const map = {};
//   list.forEach(({ menuKey, canRead, canCreate, canUpdate, canDelete }) => {
//     map[menuKey] = {
//       read: canRead,
//       create: canCreate,
//       update: canUpdate,
//       delete: canDelete,
//     };
//   });
//   return map;
// };

// // ─── Provider ────────────────────────────────────────────────────────────────
// export const PermissionProvider = ({ children }) => {
//   const [permissions, setPermissions] = useState({});
//   const [loading, setLoading] = useState(true);

//   const load = useCallback(async () => {
//     const user = getUserDetails();

//     // Super admins bypass permission checks — grant everything implicitly
//     if (
//       user?.role === "ROLE_SUPER_ADMIN" ||
//       user?.roles === "ROLE_SUPER_ADMIN"
//     ) {
//       setPermissions({ _superAdmin: true });
//       setLoading(false);
//       return;
//     }

//     const roleUuid = user?.roleUuid;
//     if (!roleUuid) {
//       setPermissions({});
//       setLoading(false);
//       return;
//     }

//     try {
//       const res = await fetchRoleAccess(roleUuid);
//       setPermissions(buildPermMap(res.data?.data || []));
//     } catch {
//       setPermissions({});
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   useEffect(() => {
//     load();
//   }, [load]);

//   /**
//    * Check a single permission.
//    * @param {string} menuKey   e.g. "CHECKLIST_BUILDER"
//    * @param {string} action    "read" | "create" | "update" | "delete"
//    */
//   const can = useCallback(
//     (menuKey, action = "read") => {
//       if (permissions._superAdmin) return true;
//       return !!permissions[menuKey]?.[action];
//     },
//     [permissions]
//   );

//   return (
//     <PermissionContext.Provider value={{ permissions, loading, reload: load, can }}>
//       {children}
//     </PermissionContext.Provider>
//   );
// };

// // ─── Hook ─────────────────────────────────────────────────────────────────────
// export const usePermissions = () => useContext(PermissionContext);

// export default PermissionContext;

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import Cookies from "js-cookie";
import { fetchRole } from "../configuration/module/Role/Service/roleService";
import { fetchRoleAccess } from "../configuration/module/UserAccess/Service/Useraccessservice";

// ─── Context ──────────────────────────────────────────────────────────────────
const PermissionContext = createContext({
  permissions: {},
  loading: true,
  reload: () => {},
  can: () => false,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getUserDetails = () => {
  try {
    const raw = Cookies.get("user_details");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const SUPER_ADMIN_ROLE = "ROLE_SUPER_ADMIN";

const buildPermMap = (list = []) => {
  const map = {};
  list.forEach(({ menuKey, canRead, canCreate, canUpdate, canDelete }) => {
    map[menuKey] = {
      read:   canRead,
      create: canCreate,
      update: canUpdate,
      delete: canDelete,
    };
  });
  return map;
};

// ─── Provider ─────────────────────────────────────────────────────────────────
export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading]         = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const user = getUserDetails();

    // No cookie — not logged in yet
    if (!user) {
      setPermissions({});
      setLoading(false);
      return;
    }

    // Super admin shortcut — bypass API call entirely
    const roleName = user?.role || user?.roles || "";
    if (roleName === SUPER_ADMIN_ROLE) {
      setPermissions({ _superAdmin: true });
      setLoading(false);
      return;
    }

    // ── Step 1: try roleUuid directly from cookie (present after backend patch) ──
    let roleUuid = user?.roleUuid;

    // ── Step 2: fallback — resolve UUID by fetching roles and matching by name ──
    // This handles the case where the backend patch has NOT been applied yet,
    // or the user has an old cookie from before the patch.
    if (!roleUuid && roleName) {
      try {
        const res = await fetchRole({ page: 0, size: 100, search: roleName });
        const match = (res.data?.content || []).find(
          (r) => r.roleName === roleName
        );
        if (match?.uuid) {
          roleUuid = match.uuid;
        }
      } catch {
        // ignore — will fall through to empty permissions
      }
    }

    if (!roleUuid) {
      // Still no UUID — user has no role assigned or API failed
      setPermissions({});
      setLoading(false);
      return;
    }

    // ── Step 3: fetch permissions for this role ──
    try {
      const res = await fetchRoleAccess(roleUuid);
      const list = res.data?.data || [];
      setPermissions(buildPermMap(list));
    } catch {
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, []); // intentionally empty — `load` identity is stable; callers use `reload`

  // Run on mount (covers page refresh) and whenever the cookie value changes
  useEffect(() => {
    load();
  }, [load]);

  const can = useCallback(
    (menuKey, action = "read") => {
      if (permissions._superAdmin) return true;
      return !!permissions[menuKey]?.[action];
    },
    [permissions]
  );

  return (
    <PermissionContext.Provider value={{ permissions, loading, reload: load, can }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionContext);

export default PermissionContext;